import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { setFiltersCollapsed as setFiltersCollapsedRedux } from '../../../../../store/screeningSpaceSlice';
import axios from "axios";
import GetCookie from "../../../../utils/GetCookie";
import ErrorPopup from "../../../../utils/popup/ErrorPopup";
import ProcessingPopup from "../../../../utils/popup/ProcessingPopup";
import SuccessPopup from "../../../../utils/popup/SuccessPopup";
import DocumentPreview from "../../../../utils/DocumentPreview";
import { PICO_COLORS, normalizePicoJson, buildTagOptionsFromPicoJson } from "../../../../utils/Picocolors";
import OpenReviewPanel from "./OpenReviewPanel";
import AIResponsePanel from "./AIResponsePanel";
import ScreeningView from "./ScreeningView";
import ConfirmationModal from "../../../../utils/ConfirmationModal";

const Screening = ({
  mode = "HUMAN",
  stats = {},
  fabricId,
  picoJson,
  onScreeningUpdate,
  abstractScreening = null,
  fullTextScreening = null,
  framework = null,
  isOwner = false,
  // Owned by ScreeningSpaceProjectView so the sidebar counts (which come from
  // fabric-details) refetch alongside this list when the toggle moves. Falls
  // back to local state if this component is mounted without the parent wiring.
  reviewerSet: reviewerSetProp,
  onReviewerSetChange: onReviewerSetChangeProp,
}) => {
  const API_BASE_URL = "/pceapi/slr";

  const headers = useMemo(
    () => ({
      Authorization: GetCookie("access_token"),
    }),
    [],
  );

  const dispatch = useDispatch();
  const filtersCollapsed = useSelector((state) => state.screeningSpace.filtersCollapsed);

  const [panelOpen, setPanelOpen] = useState(false);
  const [activeCardId, setActiveCardId] = useState(null);

  const [listData, setListData] = useState([]);
  // Project owner who is not an allocated reviewer. The backend serves them the
  // whole project read-only, so every write-side control is hidden rather than
  // offered and then refused server-side.
  const [readOnly, setReadOnly] = useState(false);

  // For DUAL_HUMAN_HUMAN: owner screens as SET_1 or SET_2 (UI toggle).
  // Default SET_1. Passed to save-decision so backend uses correct conflict slot.
  const [localReviewerSet, setLocalReviewerSet] = useState("SET_1");
  const reviewerSet = reviewerSetProp ?? localReviewerSet;
  const [counts, setCounts] = useState({
    total: 0,
    included: 0,
    excluded: 0,
    completed: 0,
    pending: 0,
    unresolved: 0,
    conflicts: 0,
  });

  const [pagination, setPagination] = useState({
    page: 1,
    size: 25,
    total: 0,
    total_pages: 1,
  });

  const [navData, setNavData] = useState({
    next_id: null,
    previous_id: null,
    current_index: 0,
    total: 0,
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [loading, setLoading] = useState(false);
  const [saveHandler, setSaveHandler] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [decisionFilter, setDecisionFilter] = useState(null);
  const [reviewerFilter, setReviewerFilter] = useState(null);
  const [reasonFilter, setReasonFilter] = useState(null);
  const [reasonReady, setReasonReady] = useState(true); // false = waiting for reason selection after agent picked
  const [selectedTags, setSelectedTags] = useState([]);
  const [showAIScreeningConfirmation, setShowAIScreeningConfirmation] =
    useState(false);
  const [aiRunType, setAiRunType] = useState("");
  const [aiSelectedIds, setAiSelectedIds] = useState(null);
  const [isDownloadingQC, setIsDownloadingQC] = useState(false);
  // Full-Text QC Add Paper — direct multi-PDF upload with migration-style status.
  const [addPaperStatus, setAddPaperStatus] = useState(null); // null | RUNNING | COMPLETED | FAILED
  const [isAddingPapers, setIsAddingPapers] = useState(false);
  const [addPaperProgress, setAddPaperProgress] = useState({ done: 0, total: 0 });

  // Bulk decision confirmation
  const [showBulkConfirmation, setShowBulkConfirmation] = useState(false);
  const [pendingBulkAction, setPendingBulkAction] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterSummary, setFilterSummary] = useState(null);

  const [isProcessingPopupVisible, setIsProcessingPopupVisible] =
    useState(false);
  const [processingMessage, setProcessingMessage] = useState("");

  // Addeed isStopping state 
  const [isStopping, setIsStopping] = useState(false);

  const [confidenceStart, setConfidenceStart] = useState(null);
  const [clearKey, setClearKey] = useState(0);
  const [confidenceEnd, setConfidenceEnd] = useState(null);
  const hasAI = ["SINGLE_AI_ONLY", "DUAL_HUMAN_AI"].includes(framework);

  // Advanced filter state — lifted here so it's included in all list fetches
  const [advancedEnabled, setAdvancedEnabled] = useState(false);
  const [advConditions, setAdvConditions] = useState([
    { id: 1, category: "", filterOp: "contains", connector: "AND", value: "" },
  ]);

  // ── NEW: Export / Import screening template (Human screening only) ──────
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportScope, setExportScope] = useState("ALL");
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const [showImportReport, setShowImportReport] = useState(false);
  // ──────────────────────────────────────────────────────────────────────────

  const activeFilterCount =
    (searchQuery ? 1 : 0) +
    (dateFrom ? 1 : 0) +
    (dateTo ? 1 : 0) +
    (decisionFilter ? 1 : 0) +
    selectedTags.length +
    (confidenceStart !== null ? 1 : 0) +
    (confidenceEnd !== null ? 1 : 0) +
    (advancedEnabled ? advConditions.filter(c => c.category || c.value).length : 0);

  const clearAllFilters = () => {
    setSearchInput("");
    setSearchQuery("");
    setDateFrom("");
    setDateTo("");
    setDecisionFilter(null);
    setReviewerFilter(null);
    setReasonFilter(null);
    setReasonReady(true);
    setSelectedTags([]);
    setConfidenceStart(null);
    setConfidenceEnd(null);
    setAdvancedEnabled(false);
    setAdvConditions([{ id: 1, category: "", filterOp: "contains", connector: "AND", value: "" }]);
    setPage(1);
    setClearKey((k) => k + 1);
  };

  // Reset reason filter when decision moves away from INCLUDED/EXCLUDED
  useEffect(() => {
    if (decisionFilter !== "INCLUDED" && decisionFilter !== "EXCLUDED") {
      setReasonFilter(null);
      setReasonReady(true);
    }
  }, [decisionFilter]);

  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState("");

  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState("");
  const [showQCCompleteConfirmation, setShowQCCompleteConfirmation] =
    useState(false);


  const startProcessing = (msg) => {
    setProcessingMessage(msg || "Processing...");
    setIsProcessingPopupVisible(true);
  };

  const stopProcessing = () => {
    setIsProcessingPopupVisible(false);
    setProcessingMessage("");
  };

  const showError = (msg) => {
    setPopupErrorMessage(msg || "Something went wrong");
    setIsErrorPopupVisible(true);
  };

  const showSuccess = (msg) => {
    setPopupSuccessMessage(msg || "Action completed successfully");
    setIsSuccessPopupVisible(true);
  };

  const closeErrorPopup = () => setIsErrorPopupVisible(false);
  const closeSuccessPopup = () => setIsSuccessPopupVisible(false);

  const normalizedPicoJson = useMemo(() => normalizePicoJson(picoJson), [picoJson]);

  // Build tagOptions in TagFilterPanel-compatible format
  const tagOptions = useMemo(() => buildTagOptionsFromPicoJson(picoJson), [picoJson]);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewS3Path, setPreviewS3Path] = useState(null);
  const [previewDocumentName, setPreviewDocumentName] = useState("");

  const isConflict = mode === "CONFLICT";
  const isQC = mode === "QC";
  const isAI_FT = mode === "AI_FT";
  const isConflict_FT = mode === "CONFLICT_FT";
  const isQC_FT = mode === "QC_FT";
  const isFullTextMode =
    mode === "HUMAN_FT" ||
    mode === "AI_FT" ||
    mode === "CONFLICT_FT" ||
    mode === "QC_FT";

  const isAI = mode === "AI" || mode === "AI_FT";

  const normalizeStatus = (s) => String(s || "").toUpperCase();

  // Priority: direct screening props (same as sidebar) > stats fallbacks
  const abstractAiStatus =
    abstractScreening?.statuses?.ai_status ||
    stats?.abstract_screening?.statuses?.ai_status ||
    stats?.statuses?.ai_status ||
    stats?.ai_status;

  const fullTextAiStatus =
    fullTextScreening?.statuses?.ai_status ||
    stats?.full_text_screening?.statuses?.ai_status ||
    stats?.statuses?.ai_status ||
    stats?.ai_status;

  const isAbstractAIScreeningRunning =
    (mode === "AI" && normalizeStatus(abstractAiStatus) === "RUNNING") ||
    (mode === "AI_FT" && normalizeStatus(fullTextAiStatus) === "RUNNING");

  // Derive the actual AI status string for the current mode (for StatusBadge)
  const currentAiStatus =
    mode === "AI"
      ? normalizeStatus(abstractAiStatus)
      : mode === "AI_FT"
        ? normalizeStatus(fullTextAiStatus)
        : null;

  const titleMap = {
    HUMAN: "Human Screening",
    AI: "AI Screening",
    CONFLICT: "Resolve Conflicts",
    QC: "Quality Check",
    HUMAN_FT: "Human Screening",
    AI_FT: "AI Screening",
    CONFLICT_FT: "Resolve Conflicts",
    QC_FT: "Quality Check",
  };

  const title = titleMap[mode];

  const screeningLabel = () => {
    if (mode === "AI") return "AI Abstract Screening";
    if (mode === "AI_FT") return "AI Full-Text Screening";
    if (mode === "HUMAN") return "Human Abstract Screening";
    if (mode === "HUMAN_FT") return "Human Full-Text Screening";
    if (mode === "CONFLICT") return "Conflict Resolution (Abstract)";
    if (mode === "CONFLICT_FT") return "Conflict Resolution (Full-Text)";
    if (mode === "QC") return "Quality Check (Abstract)";
    if (mode === "QC_FT") return "Quality Check (Full-Text)";
    return "Screening";
  };

  const isMultiModelAI = useMemo(() => {
    if (mode !== "AI" && mode !== "AI_FT") return false;

    return listData.some(
      (item) =>
        Array.isArray(item.ai_decisions) && item.ai_decisions.length > 1,
    );
  }, [mode, listData]);

  const isConflictMode = mode === "CONFLICT" || mode === "CONFLICT_FT";

  const isQCMode = mode === "QC" || mode === "QC_FT";

  const metrics = useMemo(() => {
    const base = [
      { value: counts.total, label: "Results", valueClassName: "text-primary" },
      { value: counts.included, label: "Include", valueClassName: "text-success" },
      { value: counts.excluded, label: "Exclude", valueClassName: "text-danger" },
    ];

    if (!isQCMode) {
      base.push(
        {
          value: counts.completed,
          label: isConflictMode ? "Conflicts Resolved" : "Completed",
          valueClassName: "text-primary",
        },
        {
          value: counts.pending,
          label: isConflictMode ? "Remaining Conflicts" : "Pending",
          valueClassName: "text-muted",
        },
      );
    }

    if (mode === "AI" || mode === "AI_FT") {
      base.push({
        value: counts.conflicts,
        label: "Conflicts",
        valueClassName: "text-warning",
      });
    }

    return base;
  }, [mode, counts, isQCMode, isConflictMode]);

  const getEndpoint = () => {
    if (mode === "AI") return `${API_BASE_URL}/abstract-screening-ai/list`;
    if (mode === "HUMAN")
      return `${API_BASE_URL}/abstract-screening-human/list`;
    if (mode === "CONFLICT")
      return `${API_BASE_URL}/abstract-screening-conflicts/list`;
    if (mode === "QC") return `${API_BASE_URL}/abstract-screening-qc/list`;
    if (mode === "AI_FT") return `${API_BASE_URL}/full-text-screening-ai/list`;
    if (mode === "HUMAN_FT")
      return `${API_BASE_URL}/full-text-screening-human/list`;
    if (mode === "CONFLICT_FT")
      return `${API_BASE_URL}/full-text-screening-conflicts/list`;
    if (mode === "QC_FT") return `${API_BASE_URL}/full-text-screening-qc/list`;
  };

  const fullTextIdMap = useMemo(() => {
    const map = {};
    (listData || []).forEach((item) => {
      if (item?.screening_id != null && item?.full_text_screening_id != null) {
        map[item.screening_id] = item.full_text_screening_id;
      }
      // Also create reverse mapping for QC/Conflict modes
      if (item?.full_text_screening_id != null && item?.screening_id != null) {
        map[item.full_text_screening_id] = item.screening_id;
      }
    });
    return map;
  }, [listData]);

  const extractIncomingId = (idOrObj) => {
    if (!idOrObj) return null;
    if (typeof idOrObj !== "object") return idOrObj;
    return (
      idOrObj.full_text_screening_id ??
      idOrObj.screening_id ??
      idOrObj.id ??
      idOrObj.doc_id ??
      null
    );
  };

  const resolveActiveId = (incoming) => {
    const id = extractIncomingId(incoming);
    if (!id) return id;

    console.log("Mode:", mode, "Resolving ID:", id); // Debug

    // For QC_FT and CONFLICT_FT, the ID is already screening_id (slr_full_text_screening.id)
    if (isQC_FT || isConflict_FT) {
      return id;
    }

    // For HUMAN_FT and AI_FT, navigation returns full_text_screening_id (slr_study_linking.id)
    // But we need to check if we're opening from list or navigating
    if (mode === "HUMAN_FT" || mode === "AI_FT") {
      // When navigating (ID comes from backend navigation), use it directly
      // When opening from list (ID comes from card), it might need mapping
      return id;
    }

    return id;
  };

  const openPdfPreview = (s3Path, title) => {
    if (!s3Path) {
      showError("PDF not available for this record");
      return;
    }
    setPreviewS3Path(s3Path);
    setPreviewDocumentName(title || s3Path.split("/").pop());
    setPreviewOpen(true);
  };

  const closePdfPreview = () => {
    setPreviewOpen(false);
    setPreviewS3Path(null);
    setPreviewDocumentName("");
  };

  const [isBulkLoading, setIsBulkLoading] = useState(false);
  const [criteriaList, setCriteriaList] = useState([]); // [{ id, label, type: 'include'|'exclude' }]

  useEffect(() => {
    setCriteriaList([]);
  }, [fabricId, mode]);

  // Fetch available criteria directly from project details (fabric-level, no item needed)
  useEffect(() => {
    if (!fabricId) return;
    // QC included: its final decision carries reasons too, so the Reason
    // filter applies there the same way it does on the other tabs.
    if (!["HUMAN", "CONFLICT", "HUMAN_FT", "CONFLICT_FT", "AI", "AI_FT", "QC", "QC_FT"].includes(mode)) return;

    const isFT = mode === "HUMAN_FT" || mode === "CONFLICT_FT";

    axios.post(`${API_BASE_URL}/form/get-project-details`, { slr_fabric_id: fabricId }, { headers })
      .then((res) => {
        const raw = res.data?.data?.stages?.criteria?.data?.criteria_list || [];
        // Map all criteria — no phase filter, backend already scopes per fabric
        const mapped = raw.map((c) => ({
          id: c.id,
          label: c.reason_code || c.reason || "",
          type: (c.criteria || "").toUpperCase() === "INCLUSION" ? "include" : "exclude",
        })).filter((c) => c.id && c.label);
        if (mapped.length) setCriteriaList(mapped);
      })
      .catch((err) => { console.error("Criteria fetch failed:", err); });
  }, [fabricId, mode]);

  const handleBulkDecision = (decision, selectedCriteriaIds, selectedIds = null) => {
    if (!fabricId) return;
    if (qcLockedForUser) { showError(qcLockMessage); return; }
    if (decision === "EXCLUDED" && (!selectedCriteriaIds || !selectedCriteriaIds.length)) return;
    setPendingBulkAction({ decision, selectedCriteriaIds, selectedIds });
    setShowBulkConfirmation(true);
  };

  const executeBulkDecision = async () => {
    setShowBulkConfirmation(false);
    if (!pendingBulkAction) return;

    const { decision, selectedCriteriaIds, selectedIds } = pendingBulkAction;
    setPendingBulkAction(null);

    const isFT = mode === "HUMAN_FT" || mode === "CONFLICT_FT";
    const endpoint = isFT
      ? `${API_BASE_URL}/full-text-screening-human/bulk-save-decision`
      : `${API_BASE_URL}/abstract-screening-human/bulk-save-decision`;

    const payload = {
      slr_fabric_id: fabricId,
      decision,
      criteria_ids: selectedCriteriaIds || [],
      search: searchQuery || null,
      decision_filter: decisionFilter === "ACTION_REQUIRED" ? null : (decisionFilter || null),
      start_year: dateFrom || null,
      end_year: dateTo || null,
      selected_tags: selectedTags?.length ? selectedTags : null,
      advanced_filters: buildAdvancedFilters(),
      selected_ids: selectedIds,
      // Pass reviewer_set only for owner in DUAL_HUMAN_HUMAN — backend ignores it otherwise
      reviewer_set: isOwner && framework === "DUAL_HUMAN_HUMAN" ? reviewerSet : null,
    };

    setIsBulkLoading(true);
    startProcessing(`Bulk ${decision === "INCLUDED" ? "including" : "excluding"} all filtered records...`);
    try {
      const res = await axios.post(endpoint, payload, { headers });
      if (res.data?.status === "NotAuthorized") {
        showError("Not authorized. Please log in again.");
        return;
      }
      if (res.data?.status === "success") {
        showSuccess(res.data?.message || `Bulk ${decision} applied successfully`);
        fetchScreeningList();
      } else {
        showError(res.data?.message || `Failed to bulk ${decision}`);
      }
    } catch (err) {
      showError(err?.message || `Failed to bulk ${decision}`);
    } finally {
      setIsBulkLoading(false);
      stopProcessing();
    }
  };

  const handleDownloadQC = async () => {
  if (!fabricId) return;
  setIsDownloadingQC(true);
  startProcessing("Preparing download...");
  try {
    const endpoint = `${API_BASE_URL}/download-screening`;

    const payload = {
      slr_fabric_id: fabricId,
      mode: mode,
      search: searchQuery || null,
      decision_filter:
        decisionFilter === "ACTION_REQUIRED"
          ? null
          : decisionFilter || null,
      ...(decisionFilter === "ACTION_REQUIRED" && { action_required: true }),
      start_year: dateFrom || null,
      end_year: dateTo || null,
      selected_tags: selectedTags?.length ? selectedTags : null,
      reviewer_filter: reviewerFilter || null,
      confidence_start:
        hasAI && (isConflictMode || isQCMode) ? confidenceStart : null,
      confidence_end:
        hasAI && (isConflictMode || isQCMode) ? confidenceEnd : null,
      advanced_filters: buildAdvancedFilters(),
      // Same state the list filters on, so the sheet contains exactly
      // the rows shown on screen. Without this the download fell back
      // to decision-only filtering and shipped extra rows.
      criteria_filter_id: reasonFilter || null,
    };

    const res = await axios.post(endpoint, payload, {
      headers,
      responseType: "blob",
    });

    // Backend errors arrive as JSON (status/message dict) even though we
    // asked for a blob — detect and surface them before trying to download.
    const blob = res.data;
    const isJsonError = blob?.type && blob.type.includes("application/json");
    if (isJsonError) {
      const text = await blob.text();
      let message = "Failed to download results";
      try {
        const parsed = JSON.parse(text);
        message = parsed?.message || message;
      } catch {
        // keep default
      }
      showError(message);
      return;
    }

    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;

    const fileNames = {
      HUMAN: "Human_Abstract_Screening.xlsx",
      AI: "AI_Abstract_Screening.xlsx",
      CONFLICT: "Abstract_Conflict_Resolution.xlsx",
      QC: "Abstract_QC_Results.xlsx",
      HUMAN_FT: "Human_FullText_Screening.xlsx",
      AI_FT: "AI_FullText_Screening.xlsx",
      CONFLICT_FT: "FullText_Conflict_Resolution.xlsx",
      QC_FT: "FullText_QC_Results.xlsx",
    };

    // Prefer the filename the backend sends (it now includes the project name,
    // e.g. "MyProject_Human_Abstract_Screening.xlsx"). Blob downloads don't use
    // response headers automatically — we have to parse it ourselves and set it
    // on the <a download> attribute. Fall back to the generic per-mode name if
    // the header is missing (e.g. blocked by a proxy/CORS config).
    let filename = fileNames[mode] || "SLR_SCREENING.xlsx";
    const disposition =
      res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"];
    if (disposition) {
      const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
      const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
      const rawName = utf8Match?.[1] || plainMatch?.[1];
      if (rawName) {
        try {
          filename = decodeURIComponent(rawName);
        } catch {
          filename = rawName;
        }
      }
    }

    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();

    showSuccess("Results downloaded");
  } catch (err) {
    let message = "Failed to download results";
    if (err?.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        const parsed = JSON.parse(text);
        message = parsed?.message || message;
      } catch {
        // keep default
      }
    } else {
      message = err?.response?.data?.message || err?.message || message;
    }
    showError(message);
  } finally {
    setIsDownloadingQC(false);
    stopProcessing();
  }
};


  // ── NEW: Export screening template (Human screening only) ───────────────
  const handleExportTemplate = async () => {
    if (!fabricId) return;
    setShowExportModal(false);
    setIsExporting(true);
    startProcessing("Preparing screening template...");

    try {
      const res = await axios.post(
        // Full-text has its own template (both criteria types, reason always required).
        mode === "HUMAN_FT"
          ? `${API_BASE_URL}/full-text-screening-human/export-template`
          : `${API_BASE_URL}/abstract-screening-human/export-template`,
        { slr_fabric_id: fabricId, scope: exportScope },
        { headers, responseType: "blob" },
      );

      const blob = res.data;

      // If the backend errored out, it returns JSON (status/message dict),
      // not an xlsx file. Axios still hands it back as a Blob because we
      // asked for responseType: "blob" — detect that case before downloading.
      const isJsonError =
        blob?.type && blob.type.includes("application/json");

      if (isJsonError) {
        const text = await blob.text();
        let message = "Failed to export screening template";
        try {
          const parsed = JSON.parse(text);
          message = parsed?.message || message;
        } catch {
          // leave default message if body wasn't parseable JSON either
        }
        showError(message);
        return;
      }

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        mode === "HUMAN_FT"
          ? `slr_full_text_screening_template_${fabricId}.xlsx`
          : `slr_human_screening_template_${fabricId}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      showSuccess("Screening template downloaded");
    } catch (err) {
      // err.response.data is also a Blob here since responseType is "blob" —
      // read it as text to get the real message instead of showing [object Blob]
      let message = "Failed to export screening template";
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          message = parsed?.message || message;
        } catch {
          // fall through to default message
        }
      } else {
        message = err?.response?.data?.message || err?.message || message;
      }
      showError(message);
    } finally {
      setIsExporting(false);
      stopProcessing();
    }
  };

  // ── NEW: Import filled-in screening decisions (Human screening only) ────
  const handleImportFileSelected = async (file) => {
    if (!file || !fabricId) return;
    if (qcLockedForUser) { showError(qcLockMessage); return; }
    setIsImporting(true);
    startProcessing("Importing screening decisions...");

    try {
      const formData = new FormData();
      formData.append("slr_fabric_id", fabricId);
      formData.append("file", file);
      // Creator importing under DUAL_HUMAN_HUMAN must land in the set the
      // toggle is on, same as a single or bulk save.
      if (isOwner && framework === "DUAL_HUMAN_HUMAN") {
        formData.append("reviewer_set", reviewerSet);
      }

      const res = await axios.post(
        mode === "HUMAN_FT"
          ? `${API_BASE_URL}/full-text-screening-human/import-decisions`
          : `${API_BASE_URL}/abstract-screening-human/import-decisions`,
        formData,
        { headers },
      );

      const data = res?.data;
      if (data?.status === "NotAuthorized") {
        showError("Not authorized. Please log in again.");
        return;
      }

      // A plain failure (bad file, no access) has no rows to report.
      if (data?.status === "failed" && !data?.skipped?.length) {
        showError(data?.message || "Failed to import screening decisions");
        return;
      }

      setImportReport(data);
      setShowImportReport(true);

      if (data?.status === "success" || data?.status === "partial") {
        // Close the review panel: it still holds the pre-import decision and
        // its save handler would write that back over the imported one.
        setPanelOpen(false);
        setActiveCardId(null);
        fetchScreeningList();
      }
    } catch (err) {
      showError(err.response?.data?.message || "Failed to import screening decisions");
    } finally {
      setIsImporting(false);
      stopProcessing();
    }
  };
  // ──────────────────────────────────────────────────────────────────────────

  const openPdfInNewTab = async (s3Path, docId = null) => {
    if (isFullTextMode && docId) {
      startProcessing("Opening PDF...");

      try {
        const res = await axios.post(
          `${API_BASE_URL}/full-text-screening/preview`,
          {
            fabric_id: fabricId,
            doc_id: docId,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `${GetCookie("access_token")}`,
            },
          },
        );

        if (res.data?.status !== "success" || !res.data?.preview_url) {
          showError(res.data?.message || "Failed to generate PDF preview");
          return;
        }

        window.open(res.data.preview_url, "_blank", "noopener,noreferrer");
      } catch (e) {
        console.error("Open PDF failed:", e);
        showError(
          e.response?.data?.message || e.message || "Failed to open PDF",
        );
      } finally {
        stopProcessing();
      }
      return;
    }

    if (!s3Path) {
      showError("PDF not available for this record");
      return;
    }

    startProcessing("Opening PDF...");

    try {
      const res = await axios.post(
        "/pceapi/threads/unstructured/output-document-preview",
        { s3_path: s3Path },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${GetCookie("access_token")}`,
          },
        },
      );

      if (res.data?.status !== "success" || !res.data?.preview_url) {
        showError(res.data?.message || "Failed to generate PDF preview");
        return;
      }

      window.open(res.data.preview_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error("Open PDF failed:", e);
      showError(e.response?.data?.message || e.message || "Failed to open PDF");
    } finally {
      stopProcessing();
    }
  };

  const [isQCCompleted, setIsQCCompleted] = useState(false);

  // ── QC lock ────────────────────────────────────────────────────────────
  // Once a stage's QC is marked complete:
  //   • nobody can run / restart AI for that stage
  //   • only the project creator can change human, conflict and QC decisions
  // The backend enforces both; this only mirrors it in the UI.
  const [fetchedQcCompleted, setPhaseQcCompleted] = useState(false);
  const [isProjectOwner, setIsProjectOwner] = useState(false);
  const isFullTextPhase =
    mode === "HUMAN_FT" || mode === "AI_FT" || mode === "CONFLICT_FT" || mode === "QC_FT";
  // The sidebar already passes each stage's statuses in; use them too so the
  // lock applies immediately, even before / without the fabric-details call.
  const propQcStatus = String(
    (isFullTextPhase
      ? fullTextScreening?.statuses?.qc_status ?? stats?.full_text_screening?.statuses?.qc_status
      : abstractScreening?.statuses?.qc_status ?? stats?.abstract_screening?.statuses?.qc_status) || "",
  ).toUpperCase();
  const phaseQcCompleted = fetchedQcCompleted || propQcStatus === "COMPLETED";
  const qcLockedForUser = phaseQcCompleted && !isProjectOwner;
  const aiLockedByQC = phaseQcCompleted;
  const qcLockMessage = qcLockedForUser
    ? `${isFullTextPhase ? "Full-text" : "Abstract"} QC is complete. Only the project admin can change decisions now.`
    : null;
  const aiLockMessage = `${isFullTextPhase ? "Full-text" : "Abstract"} QC is complete, so AI screening can no longer be run or restarted.`;

  useEffect(() => {
    setPhaseQcCompleted(false);
    setIsProjectOwner(false);
    if (!fabricId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(
          `${API_BASE_URL}/fabric-details`,
          { slr_fabric_id: fabricId },
          { headers },
        );
        if (cancelled || res.data?.status !== "success") return;
        const d = res.data.data;
        const phase = isFullTextPhase ? d?.full_text_screening : d?.abstract_screening;
        setPhaseQcCompleted(phase?.statuses?.qc_status === "COMPLETED");
        setIsProjectOwner(Boolean(d?.user_reviewer_status?.is_owner));
      } catch { /* silent — backend still enforces the lock */ }
    })();
    return () => { cancelled = true; };
  }, [fabricId, mode]); // eslint-disable-line

  // A locked user must never have a save handler (also stops auto-save on Next/Prev).
  const registerSaveHandler = (handler) =>
    setSaveHandler(qcLockedForUser ? null : () => handler);
  useEffect(() => {
    if (qcLockedForUser) setSaveHandler(null);
  }, [qcLockedForUser]);

  const handleMarkAsComplete = () => {
    if (!fabricId) return;
    setShowQCCompleteConfirmation(true);
  };
  useEffect(() => {
    // Reset first — project or mode genuinely changed
    setIsQCCompleted(false);

    // Then silently check whether this QC phase is already completed so the
    // button shows "Completed" on mount and after Refresh without requiring
    // the user to re-click. qc_status lives in fabric-details, not in the
    // QC list endpoint, so we fetch it separately here.
    if (!fabricId || (mode !== "QC" && mode !== "QC_FT")) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(
          `${API_BASE_URL}/fabric-details`,
          { slr_fabric_id: fabricId },
          { headers },
        );
        if (cancelled) return;
        if (res.data?.status === "success") {
          const d = res.data.data;
          const qcStatus =
            mode === "QC_FT"
              ? d?.full_text_screening?.statuses?.qc_status
              : d?.abstract_screening?.statuses?.qc_status;
          if (qcStatus === "COMPLETED") setIsQCCompleted(true);
        }
      } catch { /* silent — don't surface background check errors */ }
    })();

    return () => { cancelled = true; };
  }, [fabricId, mode]); // eslint-disable-line

  const executeMarkAsComplete = async () => {
    setShowQCCompleteConfirmation(false);

    if (!fabricId) return;

    const phase = mode === "QC_FT" ? "Full-Text QC" : "Abstract QC";

    setLoading(true);
    startProcessing(`Marking ${phase} as complete...`);

    try {
      const endpoint =
        mode === "QC_FT"
          ? `${API_BASE_URL}/full-text-screening-qc/mark-complete`
          : `${API_BASE_URL}/abstract-screening-qc/mark-complete`;

      const res = await axios.post(
        endpoint,
        { slr_fabric_id: fabricId },
        { headers },
      );

      if (res.data?.status === "success") {
        setIsQCCompleted(true);
        setPhaseQcCompleted(true);

        showSuccess(`${phase} marked as complete`);

        await fetchScreeningList();

        if (onScreeningUpdate) {
          onScreeningUpdate();
        }
      } else {
        showError(res.data?.message || `Failed to mark ${phase} as complete`);
      }
    } catch (err) {
      showError(
        err.response?.data?.message || `Failed to mark ${phase} as complete`,
      );
    } finally {
      setLoading(false);
      stopProcessing();
    }
  };

  // Shared filter payload for detail endpoints (nav-aware)
  const buildAdvancedFilters = () => {
    if (!advancedEnabled) return null;
    const rules = advConditions.filter(c => c.category || c.value).map(c => ({
      field: c.category,
      operator: c.filterOp || "contains",
      value: c.value,
      connector: c.connector || "AND",
    }));
    return rules.length ? { rules } : null;
  };

  const activeFilters = useMemo(() => ({
    search: searchQuery || null,
    decision_filter: decisionFilter === "ACTION_REQUIRED" ? null : (decisionFilter || null),
    ...(decisionFilter === "ACTION_REQUIRED" && { action_required: true }),
    criteria_filter_id: reasonFilter || null,
    start_year: dateFrom || null,
    end_year: dateTo || null,
    selected_tags: selectedTags?.length ? selectedTags : null,
    ...(hasAI && (isConflictMode || isQCMode) && {
      confidence_start: confidenceStart,
      confidence_end: confidenceEnd,
    }),
    advanced_filters: buildAdvancedFilters(),
  }), [searchQuery, decisionFilter, reasonFilter, dateFrom, dateTo, selectedTags, confidenceStart, confidenceEnd, hasAI, isConflictMode, isQCMode, advancedEnabled, advConditions]);

  // Direct Add Paper flow for Full-Text QC — status is PROJECT-WISE, NO polling.
  // The user only selects PDFs. Each upload returns a job_id immediately; the
  // backend then runs Data Collection migration -> standardization -> RAG ->
  // classification, and the consumer adds the paper as INCLUDED to every list.
  // The pill shows "Running" after upload; the status is (re)loaded only when
  // the project is opened / the page is refreshed — no background polling,
  // so the status API never competes with the other APIs.
  const activeFabricRef = useRef(fabricId); // guards against responses of a previous project
  const refreshAfterAddRef = useRef(null);
  refreshAfterAddRef.current = async () => {
    await fetchScreeningList();
    fetchBibliographicIds();
    if (onScreeningUpdate) onScreeningUpdate();
  };

  // "Bibliographic" badge: study_ids of papers added via Add Paper (project-wise).
  const [bibliographicIds, setBibliographicIds] = useState(() => new Set());
  const fetchBibliographicIds = async () => {
    const fabric = fabricId;
    if (!fabric) return;
    try {
      const res = await axios.post(
        `${API_BASE_URL}/qc/bibliographic-study-ids`,
        { slr_fabric_config_id: fabric },
        { headers }
      );
      if (activeFabricRef.current !== fabric) return;
      if (res.data?.status === "success") {
        setBibliographicIds(new Set((res.data.study_ids || []).map(String)));
      }
    } catch (err) {
      console.error("Bibliographic ids fetch failed:", err);
    }
  };

  const newAddPaperBatchId = () =>
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  // batch_id of the batch started in this page session (more PDFs join it)
  const addPaperBatchIdRef = useRef(null);

  // Project open / page refresh: drop the previous project's state and load
  // THIS project's Add Paper status once.
  useEffect(() => {
    activeFabricRef.current = fabricId;
    addPaperBatchIdRef.current = null;
    setAddPaperStatus(null);
    setIsAddingPapers(false);
    setAddPaperProgress({ done: 0, total: 0 });
    setBibliographicIds(new Set());
    fetchBibliographicIds();

    if (mode !== "QC_FT" || !fabricId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(
          `${API_BASE_URL}/qc/add-paper-status`,
          { slr_fabric_config_id: fabricId }, // no job_ids -> project status
          { headers }
        );
        if (cancelled || activeFabricRef.current !== fabricId) return;
        if (res.data?.status !== "success") return;
        const jobs = res.data.jobs || [];
        if (!jobs.length) return;
        if (res.data.mode === "running") {
          const running = jobs.filter((j) => j.status === "RUNNING");
          const batchIds = [...new Set(running.map((j) => j.batch_id))];
          addPaperBatchIdRef.current = batchIds.length === 1 ? batchIds[0] : null;
          setAddPaperStatus("RUNNING");
          setAddPaperProgress({ done: jobs.length - running.length, total: jobs.length });
        } else if (res.data.mode === "last") {
          setAddPaperStatus(jobs.some((j) => j.status === "FAILED") ? "FAILED" : "COMPLETED");
          setAddPaperProgress({ done: jobs.length, total: jobs.length });
        }
      } catch (err) {
        console.error("Add Paper status load failed:", err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fabricId, mode]);

  const handleAddPapers = async (files) => {
    if (mode !== "QC_FT" || !files?.length || !fabricId) return;
    const startFabric = fabricId;

    const pdfFiles = Array.from(files).filter(
      (file) =>
        file.type === "application/pdf" ||
        file.name?.toLowerCase().endsWith(".pdf")
    );

    if (!pdfFiles.length) {
      showError("Please select at least one PDF file.");
      return;
    }

    // Join the batch already running (from this session or the project), else start one.
    const batchId = addPaperBatchIdRef.current || newAddPaperBatchId();
    addPaperBatchIdRef.current = batchId;

    setIsAddingPapers(true);
    setAddPaperStatus("RUNNING");

    // Upload + extract (returns quickly with a job_id per PDF).
    const results = await Promise.allSettled(
      pdfFiles.map(async (file) => {
        const formData = new FormData();
        formData.append("pdf_file", file);
        formData.append("slr_fabric_config_id", startFabric);
        formData.append("batch_id", batchId);
        const res = await axios.post(
          `${API_BASE_URL}/qc/extract-paper-metadata`,
          formData,
          { headers: { ...headers, "Content-Type": "multipart/form-data" } }
        );
        if (res.data?.status !== "success" || !res.data?.job_id) {
          throw new Error(res.data?.message || `Failed to add ${file.name}`);
        }
        return res.data.job_id;
      })
    );

    // User switched project during upload: the jobs keep running in the
    // backend and show up when that project is opened again.
    if (activeFabricRef.current !== startFabric) return;
    setIsAddingPapers(false);

    const queued = results.filter((r) => r.status === "fulfilled").length;
    const failures = results
      .filter((r) => r.status === "rejected")
      .map((r) => r.reason?.message || "Upload failed");

    if (!queued) {
      // Nothing reached the pipeline — the batch is over.
      addPaperBatchIdRef.current = null;
      setAddPaperStatus("FAILED");
      showError(failures.join(" | "));
      return;
    }

    setAddPaperStatus("RUNNING");
    const msg =
      `${queued} paper${queued === 1 ? "" : "s"} uploaded — processing has started. ` +
      "Refresh the page to see the latest status; papers appear in all lists once processing completes.";
    if (failures.length) {
      showError(`${msg} ${failures.length} failed: ${failures.join(" | ")}`);
    } else {
      showSuccess(msg);
    }
  };

  const fetchScreeningList = async (pageOverride = page, reviewerSetOverride = reviewerSet) => {
    if (!fabricId) return;
    if (!reasonReady) return; // wait until reason is also explicitly selected

    const endpoint = getEndpoint();
    if (!endpoint) {
      showError(`No endpoint configured for mode: ${mode}`);
      return;
    }

    setLoading(true);
    startProcessing(`Loading ${screeningLabel()} list...`);

    try {
      const res = await axios.post(
        endpoint,
        {
          slr_fabric_id: fabricId,
          page: pageOverride,
          size: pageSize,
          // Scope list results and sidebar counts to the creator's active set.
          ...(isOwner && framework === "DUAL_HUMAN_HUMAN" &&
            (mode === "HUMAN" || mode === "HUMAN_FT") && { reviewer_set: reviewerSetOverride }),
          search: searchQuery || null,
          decision_filter: decisionFilter === "ACTION_REQUIRED" ? null : decisionFilter,
          ...(decisionFilter === "ACTION_REQUIRED" && { action_required: true }),
          ...(reviewerFilter === "ANY" && {
            decision_scope: { mode: "ANY" },
          }),
          ...(reviewerFilter === "ALL" && {
            decision_scope: { mode: "ALL" },
          }),
          ...(reviewerFilter && reviewerFilter !== "ANY" && reviewerFilter !== "ALL" && {
            decision_scope: { mode: "SPECIFIC", agent_id: parseInt(reviewerFilter, 10) },
          }),
          criteria_filter_id: reasonFilter || null,
          start_year: dateFrom || null,
          end_year: dateTo || null,
          selected_tags: selectedTags?.length ? selectedTags : null,
          ...(hasAI && (isConflictMode || isQCMode) && {
            confidence_start: confidenceStart,
            confidence_end: confidenceEnd,
          }),
          advanced_filters: buildAdvancedFilters(),
        },
        { headers },
      );

      if (res?.data?.status === "success") {
        const root = res?.data || {};
        const payload = root.data || root;

        const list =
          payload.conflicts ??
          payload.qc_list ??
          payload.qc_items ??
          payload.qc_results ??
          payload.qc_documents ??
          payload.qc_abstracts ??
          payload.abstracts ??
          payload.studies ??
          payload.documents ??
          payload.items ??
          payload.results ??
          payload.records ??
          [];

        const safeList = Array.isArray(list) ? list : [];
        setListData(safeList);
        setReadOnly(Boolean(payload.read_only));

        // Fix: pagination can be at root level OR inside payload
        const understoodPagination = root.pagination || payload.pagination || {};
        const totalCount =
          understoodPagination.total ??
          payload.total_count ??
          safeList.length ??
          0;

        const perPage =
          understoodPagination.size ?? payload.per_page ?? pageSize;

        setPagination({
          page: understoodPagination.page ?? payload.page ?? page,
          size: perPage,
          total: totalCount,
          total_pages: Math.max(
            1,
            understoodPagination.total_pages ?? Math.ceil(totalCount / perPage),
          ),
        });

        const st = payload.stats || {};
        const ct = payload.counts || {};

        const included = ct.included ?? st.included ?? 0;
        const excluded = ct.excluded ?? st.excluded ?? 0;
        const conflicts = ct.conflicts ?? st.conflicts ?? 0;
        const actionNeeded = ct.action_needed ?? st.action_needed ?? 0;

        let completed;
        let pending;

        if (mode === "CONFLICT" || mode === "CONFLICT_FT") {
          completed = ct.resolved ?? st.conflicts_resolved ?? 0;
          pending = (ct.unresolved ?? st.remaining_conflicts ?? 0) + actionNeeded;
        } else {
          completed = ct.completed ?? st.completed ?? 0;
          pending =
            (ct.pending ??
              st.pending ??
              Math.max(
                0,
                (ct.total ?? st.total_results ?? totalCount ?? 0) - completed,
              )) + actionNeeded;
        }

        const total =
          ct.total ?? st.total_results ?? totalCount ?? safeList.length ?? 0;

        // Calculate unresolved for conflict modes
        const unresolved =
          mode === "CONFLICT" || mode === "CONFLICT_FT"
            ? (st.remaining_conflicts ?? pending)
            : 0;

        setCounts((prev) => ({
          ...prev,
          total,
          included,
          excluded,
          completed,
          pending,
          unresolved,
          conflicts,
        }));

        // Recover QC completion status from API response so the button
        // shows "Completed" correctly even after a remount or page refresh.
        // API returns statuses.qc_status for abstract-QC and
        // statuses.qc_status (same key) for full-text QC.
        const statuses = payload.statuses || {};
        const qcStatusField = statuses.qc_status;
        const phaseCompleted =
          qcStatusField === "COMPLETED" ||
          payload.is_completed === true ||
          payload.phase_completed === true ||
          payload.qc_completed === true ||
          st.is_completed === true;
        if (phaseCompleted) setIsQCCompleted(true);

        if (onScreeningUpdate) onScreeningUpdate();
      } else {
        showError(
          res?.data?.message || `Failed to load ${screeningLabel()} list`,
        );
        setListData([]);
      }
    } catch (err) {
      console.error("Screening list error:", err);
      showError(
        err?.response?.data?.message ||
        err?.message ||
        `Failed to load ${screeningLabel()} list`,
      );
      setListData([]);
    } finally {
      setLoading(false);
      stopProcessing();
    }
  };

  const toggleTag = (tag) => {
    if (!tag) return;
    setSelectedTags((prev) => {
      const next = new Set(prev || []);
      const key = String(tag);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return Array.from(next);
    });
    setPage(1);
  };

  const handleSearch = () => {
    setSearchQuery(searchInput.trim());
    setPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleNavigationUpdate = (navigationData) => {
    if (navigationData) setNavData(navigationData);
  };

  // ============== PAGE NAVIGATION (for list pagination) ==============
  const handlePagePrev = () => {
    if (page > 1) setPage((p) => p - 1);
  };

  const handlePageNext = () => {
    if (page < pagination.total_pages) setPage((p) => p + 1);
  };

  // ============== PANEL NAVIGATION (for item-to-item in review panel) ==============
  const handlePanelNext = async () => {
    console.log("🔵 handlePanelNext called");
    console.log("Before save - counts:", counts);
    if (saveHandler) {
      try {
        setIsSaving(true);
        await saveHandler();
        await fetchScreeningList();
      } catch (error) {
        console.error("Auto-save failed:", error);
      } finally {
        setIsSaving(false);
      }
    }

    if (navData?.next_id) setActiveCardId(resolveActiveId(navData.next_id));
  };

  const handlePanelPrev = async () => {
    if (saveHandler) {
      try {
        setIsSaving(true);
        await saveHandler();
        await fetchScreeningList();
      } catch (error) {
        console.error("Auto-save failed:", error);
      } finally {
        setIsSaving(false);
      }
    }

    if (navData?.previous_id)
      setActiveCardId(resolveActiveId(navData.previous_id));
  };

  const handleRunAIScreening = (filterSummary = null, selectedIds = null) => {
    if (aiLockedByQC) { showError(aiLockMessage); return; }
    setAiRunType("run");
    setFilterSummary(filterSummary);
    setAiSelectedIds(selectedIds);
    setShowAIScreeningConfirmation(true);
  };

  const handleRestartAIScreening = (filterSummary = null, selectedIds = null) => {
    if (aiLockedByQC) { showError(aiLockMessage); return; }
    setAiRunType("restart");
    setFilterSummary(filterSummary);
    setAiSelectedIds(selectedIds);
    setShowAIScreeningConfirmation(true);
  };

  const handleStopAIScreening = async () => {
    if (!fabricId) return;

    setLoading(true);
    startProcessing("Stopping AI screening...");

    try {
      const endpoint =
        mode === "AI_FT"
          ? `${API_BASE_URL}/full-text-screening-ai/stop`
          : `${API_BASE_URL}/abstract-screening-ai/stop`;

      const res = await axios.post(
        endpoint,
        { slr_fabric_id: fabricId },
        { headers },
      );

      if (res.data?.status === "success") {
        showSuccess("AI screening stopped");
        setIsStopping(true);           // <-- STOPPING state
        fetchScreeningList();
      } else {
        showError(res.data?.message || "Failed to stop AI screening");
      }
    } catch (err) {
      showError(err.response?.data?.message || "Failed to stop AI screening");
    } finally {
      setLoading(false);
      stopProcessing();
    }
  };

  // Added polling useEffect
  useEffect(() => {
    if (!isStopping) return;
    if (currentAiStatus !== "RUNNING") {
      setIsStopping(false);
      return;
    }
    const interval = setInterval(() => fetchScreeningList(), 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStopping, currentAiStatus]);

  const screeningTypeLabel = mode === "AI_FT" ? "Full-Text Screening" : "Abstract Screening";
  const hasAiSelection = aiSelectedIds && aiSelectedIds.length > 0;

  const getAIRunWarningText = () => {
    if (aiRunType === "restart") {
      return hasAiSelection
        ? `This action will restart AI screening and reset all existing decisions of your selected papers.`
        : `This action will restart AI screening and reset all existing decisions.`;
    }

    return hasAiSelection
      ? `This action will run the pending and failed papers of your selected papers.`
      : `This action will run pending and failed papers.`;
  };

  const executeAIScreeningRun = async () => {
    setShowAIScreeningConfirmation(false);

    if (!fabricId) {
      showError("Missing fabricId");
      return;
    }

    const restart = aiRunType === "restart";

    setLoading(true);
    startProcessing(
      restart ? "Restarting AI screening..." : "Running AI screening...",
    );

    try {
      const endpoint =
        mode === "AI_FT"
          ? `${API_BASE_URL}/full-text-screening-ai/run`
          : `${API_BASE_URL}/abstract-screening-ai/run`;

      const res = await axios.post(
        endpoint,
        {
          slr_fabric_id: fabricId,
          restart,
          ...activeFilters,
          selected_ids: aiSelectedIds,
        },
        { headers },
      );

      if (res.data?.status === "success") {
        showSuccess(
          res.data?.message ||
          (restart ? "AI screening restarted" : "AI screening started"),
        );
        fetchScreeningList();
      } else {
        showError(res.data?.message || "Failed to start AI screening");
      }
    } catch (err) {
      showError(err.response?.data?.message || "Failed to start AI screening");
    } finally {
      setLoading(false);
      stopProcessing();
    }
  };

  const handleSaveClick = async () => {
    if (qcLockedForUser) { showError(qcLockMessage); return; }
    if (!saveHandler) return;
    try {
      setIsSaving(true);
      await saveHandler();
      showSuccess("Decision saved successfully!");
    } catch (err) {
      showError(err.message || "Failed to save decision");
    } finally {
      setIsSaving(false);
    }
  };

  // Fetch data when dependencies change
  useEffect(() => {
    setActiveCardId(null);
    setPanelOpen(false);
    fetchScreeningList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    fabricId,
    page,
    pageSize,
    dateFrom,
    dateTo,
    selectedTags,
    decisionFilter,
    reviewerFilter,
    reasonFilter,
    reasonReady,
    searchQuery,
    confidenceStart,
    confidenceEnd,
    clearKey,
  ]);

  // Boundary protection: reset page if it exceeds total_pages after filtering
  useEffect(() => {
    if (pagination.total_pages > 0 && page > pagination.total_pages) {
      setPage(pagination.total_pages);
    }
  }, [pagination.total_pages, page]);

  useEffect(() => {
    setSaveHandler(null);
  }, [activeCardId, mode, fabricId]);

  const panelContent = (
    <div className="flex-grow-1 overflow-auto">
      {(mode === "HUMAN" || mode === "HUMAN_FT") && (
        <OpenReviewPanel
          key={`human-panel-${activeCardId}`}
          slrFabricId={fabricId}
          isFullText={mode === "HUMAN_FT"}
          abstractId={mode === "HUMAN" ? activeCardId : undefined}
          fullTextScreeningId={mode === "HUMAN_FT" ? activeCardId : undefined}
          onNavigationUpdate={handleNavigationUpdate}
          pico_json={picoJson}
          activeFilters={activeFilters}
          onSaveSuccess={() => {
            fetchScreeningList();
          }}
          onSaveHandlerReady={registerSaveHandler}
          readOnly={qcLockedForUser}
          reviewerSet={isOwner && framework === "DUAL_HUMAN_HUMAN" ? reviewerSet : null}
        />
      )}

      {mode === "AI" && !isFullTextMode && panelOpen && (
        <AIResponsePanel
          abstractId={activeCardId}
          slrFabricId={fabricId}
          onNavigationUpdate={handleNavigationUpdate}
          pico_json={picoJson}
          activeFilters={activeFilters}
        />
      )}

      {mode === "AI_FT" && panelOpen && (
        <AIResponsePanel
          fullTextScreeningId={activeCardId}
          slrFabricId={fabricId}
          onNavigationUpdate={handleNavigationUpdate}
          pico_json={picoJson}
          isFullText
          activeFilters={activeFilters}
        />
      )}

      {mode === "CONFLICT" && (
        <OpenReviewPanel
          key={`conflict-panel-${activeCardId}`}
          conflictMode
          abstractId={activeCardId}
          slrFabricId={fabricId}
          onNavigationUpdate={handleNavigationUpdate}
          pico_json={picoJson}
          activeFilters={activeFilters}
          onSaveSuccess={() => {
            fetchScreeningList();
          }}
          onSaveHandlerReady={registerSaveHandler}
          readOnly={qcLockedForUser}
        />
      )}

      {mode === "CONFLICT_FT" && (
        <OpenReviewPanel
          key={`conflict-ft-panel-${activeCardId}`}
          conflictMode
          slrFabricId={fabricId}
          isFullText
          fullTextScreeningId={activeCardId}
          abstractId={undefined}
          onNavigationUpdate={handleNavigationUpdate}
          pico_json={picoJson}
          activeFilters={activeFilters}
          onSaveSuccess={() => {
            fetchScreeningList();
          }}
          onSaveHandlerReady={registerSaveHandler}
          readOnly={qcLockedForUser}
        />
      )}

      {mode === "QC" && (
        <OpenReviewPanel
          key={`qc-panel-${activeCardId}`}
          isQC
          abstractId={activeCardId}
          slrFabricId={fabricId}
          onNavigationUpdate={handleNavigationUpdate}
          pico_json={picoJson}
          activeFilters={activeFilters}
          onSaveSuccess={() => {
            fetchScreeningList();
          }}
          onSaveHandlerReady={registerSaveHandler}
          readOnly={qcLockedForUser}
        />
      )}

      {mode === "QC_FT" && (
        <OpenReviewPanel
          key={`qc-ft-panel-${activeCardId}`}
          isQC
          slrFabricId={fabricId}
          isFullText
          fullTextScreeningId={activeCardId}
          abstractId={undefined}
          onNavigationUpdate={handleNavigationUpdate}
          pico_json={picoJson}
          activeFilters={activeFilters}
          onSaveSuccess={() => {
            fetchScreeningList();
          }}
          onSaveHandlerReady={registerSaveHandler}
          readOnly={qcLockedForUser}
        />
      )}
    </div>
  );

  return (
    <>
      <ScreeningView
        mode={mode}
        title={title}
        isAI={isAI}
        isQC={isQC}
        isQC_FT={isQC_FT}
        isConflict={isConflict}
        isConflict_FT={isConflict_FT}
        isFullTextMode={isFullTextMode}
        picoColors={PICO_COLORS}
        tagOptions={tagOptions}
        selectedTags={selectedTags}
        onToggleTag={toggleTag}
        /* state */
        panelOpen={panelOpen}
        setPanelOpen={setPanelOpen}
        navData={navData}
        counts={counts}
        metrics={metrics}
        loading={loading}
        listData={listData}
        pagination={pagination}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(1);
        }}
        dateFrom={dateFrom}
        setDateFrom={(v) => {
          setDateFrom(v);
          setPage(1);
        }}
        dateTo={dateTo}
        setDateTo={(v) => {
          setDateTo(v);
          setPage(1);
        }}
        decisionFilter={decisionFilter}
        setDecisionFilter={(v) => {
          setDecisionFilter(v);
          setReviewerFilter(null);
          setPage(1);
        }}
        reviewerFilter={reviewerFilter}
        setReviewerFilter={(v) => {
          setReviewerFilter(v);
          setPage(1);
        }}
        reasonFilter={reasonFilter}
        setReasonFilter={(v) => {
          setReasonFilter(v);
          setReasonReady(true);
          setPage(1);
        }}
        setReasonReady={setReasonReady}
        /* Search props */
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        onSearch={handleSearch}
        onSearchKeyPress={handleSearchKeyPress}
        activeCardId={activeCardId}
        setActiveCardId={setActiveCardId}
        /* Panel navigation (item-to-item in review panel) */
        onPanelPrev={handlePanelPrev}
        onPanelNext={handlePanelNext}
        /* Page navigation (list pagination) */
        onPagePrev={handlePagePrev}
        onPageNext={handlePageNext}
        onRunAI={handleRunAIScreening}
        onRestartAI={handleRestartAIScreening}
        onStopAI={handleStopAIScreening}
        onDownloadQC={handleDownloadQC}
        onAddPapers={handleAddPapers}
        bibliographicIds={bibliographicIds}
        addPaperStatus={addPaperStatus}
        isAddingPapers={isAddingPapers}
        addPaperProgress={addPaperProgress}
        onMarkComplete={handleMarkAsComplete}
        isQCCompleted={isQCCompleted}
        onRefresh={() => {
          fetchScreeningList();
        }}
        onPreviewPdf={(s3Path, titleText, docId) =>
          openPdfInNewTab(s3Path, docId)
        }
        onOpenPanel={(idOrObj) => {
          const resolved = resolveActiveId(idOrObj);
          if (resolved && typeof resolved !== "object") {
            setActiveCardId(resolved);
          }
          setPanelOpen(true);
        }}
        /* save */
        showSaveButton={mode !== "AI" && mode !== "AI_FT" && !readOnly && !qcLockedForUser}
        canSave={!!saveHandler}
        isSaving={isSaving}
        onSaveClick={handleSaveClick}
        /* panel */
        panelContent={panelContent}
        isRunDisabled={isAbstractAIScreeningRunning}
        // Updated the aiStatus prop pass
        aiStatus={isStopping ? "STOPPING" : currentAiStatus}
        isDownloadingQC={isDownloadingQC}
        hasAI={hasAI}
        confidenceStart={confidenceStart}
        setConfidenceStart={(v) => {
          setConfidenceStart(v);
          setPage(1);
        }}
        confidenceEnd={confidenceEnd}
        setConfidenceEnd={(v) => {
          setConfidenceEnd(v);
          setPage(1);
        }}
        filtersCollapsed={filtersCollapsed}
        setFiltersCollapsed={(val) => {
          const newVal = typeof val === 'function' ? val(filtersCollapsed) : val;
          dispatch(setFiltersCollapsedRedux(newVal));
        }}
        advancedEnabled={advancedEnabled}
        setAdvancedEnabled={(val) => {
          setAdvancedEnabled(val);
        }}
        advConditions={advConditions}
        setAdvConditions={setAdvConditions}
        onApplyAdvancedFilters={() => {
          setPage(1);
          fetchScreeningList();
        }}
        onBulkDecision={handleBulkDecision}
        criteriaList={criteriaList}
        isBulkLoading={isBulkLoading}
        activeFilterCount={activeFilterCount}
        onClearAllFilters={clearAllFilters}
        /* NEW: Export / Import screening template (Human screening only) */
        onOpenExportModal={() => setShowExportModal(true)}
        onImportFile={handleImportFileSelected}
        readOnly={readOnly || qcLockedForUser}
        aiLockedByQC={aiLockedByQC}
        aiLockMessage={aiLockMessage}
        qcLockMessage={qcLockMessage}
        isExporting={isExporting}
        isImporting={isImporting}
        /* Creator reviewer-set toggle */
        isOwner={isOwner}
        framework={framework}
        reviewerSet={reviewerSet}
        onReviewerSetChange={(nextSet) => {
          if (nextSet === reviewerSet) return;
          setLocalReviewerSet(nextSet);
          // Tell the parent as well — it re-fetches fabric-details so the
          // sidebar counts follow the toggle instead of going stale.
          onReviewerSetChangeProp?.(nextSet);
          setPage(1);
          setPanelOpen(false);
          setActiveCardId(null);
          // Fetch immediately with the new set; do not wait for state to settle.
          fetchScreeningList(1, nextSet);
        }}
      />

      <DocumentPreview
        isOpen={previewOpen}
        onClose={closePdfPreview}
        bucket_type="output"
        s3_path={previewS3Path}
        documentName={previewDocumentName}
      />

      <ConfirmationModal
        show={showBulkConfirmation}
        onClose={() => { setShowBulkConfirmation(false); setPendingBulkAction(null); }}
        onConfirm={executeBulkDecision}
        title={`Confirm Bulk ${pendingBulkAction?.decision === "INCLUDED" ? "Include" : "Exclude"}`}
        message={
          pendingBulkAction?.selectedIds
            ? `This will ${pendingBulkAction?.decision === "INCLUDED" ? "include" : "exclude"} ${pendingBulkAction.selectedIds.length} selected paper(s). Continue?`
            : `This will ${pendingBulkAction?.decision === "INCLUDED" ? "include" : "exclude"} all papers matching current filters. Continue?`
        }
        confirmButtonText={pendingBulkAction?.decision === "INCLUDED" ? "Yes, Include" : "Yes, Exclude"}
        confirmButtonClass={pendingBulkAction?.decision === "INCLUDED" ? "btn-success" : "btn-danger"}
      />

      <ConfirmationModal
        show={showAIScreeningConfirmation}
        onClose={() => setShowAIScreeningConfirmation(false)}
        onConfirm={executeAIScreeningRun}
        title={`Confirm ${aiRunType === "restart" ? "Restart" : "Run"} AI Screening`}
        message={
          aiRunType === "restart"
            ? hasAiSelection
              ? "This will clear all existing AI decisions and reprocess your selected papers from scratch. This action cannot be undone."
              : "This will clear all existing AI decisions and reprocess every paper from scratch. This action cannot be undone."
            : "Do you want to continue with AI screening?"
        }
        confirmButtonText={
          aiRunType === "restart" ? "Yes, Restart" : "Yes, Run"
        }
        confirmButtonClass={
          aiRunType === "restart" ? "btn-warning" : "btn-primary"
        }
        bodyContent={
          <div>
            <div className="ev-alert-card-warning mb-2">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <strong>Important:</strong> {getAIRunWarningText()}
            </div>
            {filterSummary && (
              <div className="ev-message-card5 ev-radius-xsmall px-2 py-1 mb-2 ev-small-p-dark">
                <i className="bi bi-funnel me-1"></i>
                Only papers matching active filters will be processed:{" "}
                <strong>{filterSummary}</strong>
              </div>
            )}
            <div className="mb-1">
              <strong>Fabric ID:</strong> {fabricId}
            </div>
          </div>
        }
      />

      <ConfirmationModal
        show={showQCCompleteConfirmation}
        onClose={() => setShowQCCompleteConfirmation(false)}
        onConfirm={executeMarkAsComplete}
        title="Confirm QC Completion"
        message="Once marked as complete, this action cannot be undone. Do you want to continue?"
        confirmButtonText="Yes, Mark Complete"
        confirmButtonClass="btn-danger"
        bodyContent={
          <div>
            <p className="mb-2">
              Are you sure you want to mark{" "}
              <strong>{mode === "QC_FT" ? "Full-Text QC" : "Abstract QC"}</strong> as complete?
            </p>
            <div className="ev-alert-card-warning mb-2 p-2">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <strong>This action cannot be undone.</strong>
              <ul className="mb-0 mt-1 ps-3">
                <li>
                  {mode === "QC_FT" ? "Full-text" : "Abstract"} screening will be marked as completed.
                </li>
                <li>AI screening can no longer be run or restarted for this stage.</li>
                <li>Only the project admin will be able to change decisions after this.</li>
              </ul>
            </div>
          </div>
        }
      />

      {/* ── NEW: Export scope modal ────────────────────────────────────── */}
      <ConfirmationModal
        show={showExportModal}
        onClose={() => setShowExportModal(false)}
        onConfirm={handleExportTemplate}
        title="Export screening template"
        message=""
        confirmButtonText="Export"
        confirmButtonClass="btn-primary"
        bodyContent={
          <div>
            <p className="ev-small-p-dark mb-2">
              Choose which abstracts to include in the exported workbook.
            </p>
            <div className="d-flex flex-column gap-2">
              <label className="d-flex align-items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="exportScope"
                  checked={exportScope === "ALL"}
                  onChange={() => setExportScope("ALL")}
                />
                <span className="ev-small-p-dark">All abstracts in this fabric</span>
              </label>
              <label className="d-flex align-items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="exportScope"
                  checked={exportScope === "PENDING"}
                  onChange={() => setExportScope("PENDING")}
                />
                <span className="ev-small-p-dark">Only undecided (pending) abstracts</span>
              </label>
            </div>
          </div>
        }
      />

      {/* ── NEW: Import results modal ──────────────────────────────────── */}
      <ConfirmationModal
        show={showImportReport}
        onClose={() => setShowImportReport(false)}
        onConfirm={() => setShowImportReport(false)}
        title="Import results"
        message=""
        showCancel={false}
        confirmButtonText="Close"
        bodyContent={
          importReport && (
            <div>
              <p className="ev-small-p-dark mb-2">{importReport.message}</p>

              <div className="d-flex gap-2 mb-3">
                <span className="ev-message-card3 ev-btn-xsmall px-2 py-1 ev-radius-xsmall">
                  Applied: {importReport.applied_count ?? 0}
                </span>
                <span className="ev-message-card3 ev-btn-xsmall px-2 py-1 ev-radius-xsmall">
                  Skipped: {importReport.skipped_count ?? 0}
                </span>
                <span className="ev-message-card3 ev-btn-xsmall px-2 py-1 ev-radius-xsmall">
                  Manual criteria: {importReport.manual_criteria_flagged?.length ?? 0}
                </span>
              </div>

              {importReport.skipped?.length > 0 && (
                <div className="mb-3">
                  <p className="ev-bold-med ev-small-p-dark mb-1">Skipped rows</p>
                  <div className="scrollable-element pe-1" style={{ maxHeight: "20vh", overflowY: "auto" }}>
                    {importReport.skipped.map((s, idx) => (
                      <div key={idx} className="ev-card-2 ev-radius-xsmall p-2 mb-1">
                        <span className="ev-xsmall-p-dark">
                          Row {s.row} (Study ID {s.study_id ?? "—"}): {s.reason}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {importReport.manual_criteria_flagged?.length > 0 && (
                <div>
                  <p className="ev-bold-med ev-small-p-dark mb-1">
                    Rows with manual (non-standard) criteria
                  </p>
                  <div className="scrollable-element pe-1" style={{ maxHeight: "20vh", overflowY: "auto" }}>
                    {importReport.manual_criteria_flagged.map((m, idx) => (
                      <div key={idx} className="ev-card-2 ev-radius-xsmall p-2 mb-1">
                        <span className="ev-xsmall-p-dark">
                          Row {m.row} (Study ID {m.study_id}):{" "}
                          {Array.isArray(m.criteria_text) ? m.criteria_text.join("; ") : String(m.criteria_text ?? "")}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        }
      />

      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}

      {isErrorPopupVisible && (
        <ErrorPopup onClose={closeErrorPopup} message={popupErrorMessage} />
      )}

      {isSuccessPopupVisible && (
        <SuccessPopup
          onClose={closeSuccessPopup}
          message={popupSuccessMessage}
        />
      )}

    </>
  );
};

export default Screening;